import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Fade } from "react-awesome-reveal";
import "swiper/css";

import SectionTitle from "../home/SectionTitle";
import SliderLine from "../home/SliderLine";

import controlTrader from "../../img/trading/control-trader.webp";
import controlCity from "../../img/trading/control-city.webp";

const FEATURES = [
  { titleKey: "trading.exec.f2.title", textKey: "trading.exec.f2.text" },
  { titleKey: "trading.exec.f1.title", textKey: "trading.exec.f1.text" },
  { titleKey: "trading.exec.f3.title", textKey: "trading.exec.f3.text" },
  { titleKey: "trading.exec.f4.title", textKey: "trading.exec.f4.text" },
];

function FeatureItem({ titleKey, textKey, t, white = false }) {
  return (
    <div className="flex flex-col gap-[15px] max-w-[309px]">
      <div className="flex items-center gap-[15px]">
        <div
          className={`w-[18px] h-[18px] rounded-[2px] flex items-center justify-center shrink-0 ${
            white ? "bg-white" : "bg-[#bfccd7]"
          }`}
        >
          <Check className="w-[12px] h-[12px] text-[#1c385f]" strokeWidth={2.5} />
        </div>
        <span
          className={`font-['Inter'] font-bold text-[16px] ${
            white ? "text-white" : "text-[#1c385f]"
          }`}
        >
          {t(titleKey)}
        </span>
      </div>
      <p
        className={`font-['Inter'] font-normal text-[14px] leading-[1.5] ${
          white ? "text-white" : "text-[#435c7f]"
        }`}
      >
        {t(textKey)}
      </p>
    </div>
  );
}

function ExecutionControl() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [current, setCurrent] = useState(0);

  return (
    <section className="mt">
      <div className="glcontainer">
        <SectionTitle
          title1={t("trading.exec.title1")}
          title2={t("trading.exec.title2")}
          text={t("trading.exec.subtitle")}
          className="mx-auto"
        />

        {/* Desktop: мозаика */}
        <Fade triggerOnce direction="up" duration={700}>
        <div className="hidden lg:block relative mt-[60px] h-[413px]">
          {/* Левая карточка с фото трейдера */}
          <div className="absolute left-0 top-0 w-[34.3%] h-full rounded-[25px] overflow-hidden">
            <img
              src={controlTrader}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-[50%_25%]"
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(0,0,0,0) 43%, rgba(5,15,23,0.32) 66%, rgba(6,19,30,0.8) 100%)",
              }}
            />
            <div className="absolute left-[44px] bottom-[38px] right-[24px]">
              <FeatureItem titleKey="trading.exec.f1.title" textKey="trading.exec.f1.text" t={t} white />
            </div>
          </div>

          {/* Середина: серый + синий блоки */}
          <div className="absolute left-[34.3%] top-[31px] w-[32.9%] h-[175px] bg-[#f2f2f1] flex items-center px-[27px]">
            <FeatureItem titleKey="trading.exec.f2.title" textKey="trading.exec.f2.text" t={t} />
          </div>
          <div className="absolute left-[34.3%] top-[206px] w-[32.9%] h-[176px] bg-[#1c385f] flex items-center px-[27px]">
            <FeatureItem titleKey="trading.exec.f3.title" textKey="trading.exec.f3.text" t={t} white />
          </div>

          {/* Правая карточка с городом и тёмным оверлеем */}
          <div className="absolute left-[67.2%] top-[31px] right-0 h-[351px] rounded-r-[25px] overflow-hidden">
            <img
              src={controlCity}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-[50%_27%]"
            />
            <div className="absolute inset-0 bg-[rgba(10,22,40,0.8)]" />
            <div className="absolute left-[30px] bottom-[30px] right-[24px]">
              <FeatureItem titleKey="trading.exec.f4.title" textKey="trading.exec.f4.text" t={t} white />
            </div>
          </div>
        </div>
        </Fade>

        {/* Mobile / tablet: слайдер карточек */}
        <div className="lg:hidden mt-[25px]">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
            spaceBetween={16}
            slidesPerView={1}
            grabCursor
            breakpoints={{ 640: { slidesPerView: 2, spaceBetween: 20 } }}
          >
            {FEATURES.map((f) => (
              <SwiperSlide key={f.titleKey} className="!h-auto">
                <div className="h-full bg-[#f8faff] rounded-[20px] p-[20px]">
                  <FeatureItem titleKey={f.titleKey} textKey={f.textKey} t={t} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <SliderLine
            current={current}
            total={FEATURES.length}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}

export default ExecutionControl;
